import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Subscription } from 'rxjs';

import { SofReceivingStateService } from '../../../domain/sof-receiving/sof-receiving-state.service';
import { SofPoNumber } from '../../../models/sof-receiving/SofPoNumber.interface';
import { SofItemEdit } from '../../../models/sof-receiving/SofReceivingLogItem.interface';
import { LogEntryPhoto } from '../../../models/logs/photos/LogEntryPhoto.interface';
import {
  statusChip,
  StatusChip,
  isOnHold,
  canComplete,
  canDeleteSofPhotos,
} from '../../../domain/sof-receiving/rules';
import { PoReportViewerComponent } from '../po-report-viewer/po-report-viewer.component';

/**
 * @component SofDetailComponent
 *
 * The SOF detail / picking screen. Two steps (mobile and desktop both):
 *
 *   1. PICK — every PO on the SOF is shown with its items; the picker checks
 *      across all POs on one screen. "Save progress" persists + marks the
 *      pulled POs In Progress without leaving. "Next" advances to stage.
 *   2. STAGE — lists what's being pulled, takes the free-text staging area
 *      (one value for the whole SOF), then "Mark ready for delivery" completes
 *      every PO with pulled items and shows the confirmation panel.
 *
 * In `readOnly` (history) mode there is no pick/stage flow — items render
 * disabled, no notes editing, no staging, no actions.
 */
@Component({
  selector: 'app-sof-detail',
  templateUrl: './sof-detail.component.html',
  styleUrls: ['./sof-detail.component.css'],
})
export class SofDetailComponent implements OnInit, OnDestroy {
  @Input() readOnly = false;
  @Input() isDesktop = false;

  @Output() backToQueue = new EventEmitter<void>();

  /** Which step of the flow is showing. */
  public step: 'pick' | 'stage' = 'pick';

  /** Item id whose notes editor is expanded (mobile rows). */
  public openNotesItemId: number | null = null;

  private _orderSub!: Subscription;

  constructor(
    public state: SofReceivingStateService,
    private _modalController: ModalController
  ) {}

  ngOnInit(): void {
    // Reset to the pick step whenever a different SOF is opened.
    this._orderSub = this.state.selectedOrder.subscribe(() => {
      this.step = 'pick';
      this.openNotesItemId = null;
    });
  }

  ngOnDestroy(): void {
    this._orderSub?.unsubscribe();
  }

  public chipFor(receivingStatus: string | null | undefined): StatusChip {
    return statusChip(receivingStatus);
  }

  public get onHold(): boolean {
    return isOnHold(this.state.sofDetails.value?.orderStatus);
  }

  public get canMarkComplete(): boolean {
    return canComplete(this.state.stagingArea) && this.state.checkedCount > 0;
  }

  public isPartial(po: SofPoNumber): boolean {
    return !!po.partialNotes?.trim();
  }

  /**
   * Per-PO receiving status chip from the `staging` field (SOF.ReceivingSOFPoNumber
   * derives it: 'Not Started' / 'Started Staging' / 'Complete'). Lets Complete
   * and Started POs read differently in the list.
   */
  public poStatusChip(po: SofPoNumber): { label: string; color: string } {
    const s = (po.staging ?? '').trim().toLowerCase();
    if (s === 'complete') {
      return { label: 'Complete', color: 'success' };
    }
    if (s === 'started staging' || s === 'in progress' || s === 'started') {
      return { label: 'Started', color: 'warning' };
    }
    return { label: 'Not started', color: 'medium' };
  }

  /** Items for a PO (cross-PO screen groups items under their PO card). */
  public itemsForPo(po: SofPoNumber): SofItemEdit[] {
    return this.state.itemsForPo(po.poNumber);
  }

  /** Splits an item's Location value into the mono pin chips. */
  public locationChips(item: SofItemEdit): string[] {
    return (item.location ?? '')
      .split(',')
      .map((loc) => loc.trim())
      .filter((loc) => loc !== '');
  }

  /**
   * Whether all of a PO's still-toggleable items are checked (drives that PO's
   * select-all). Already-pulled/locked items are excluded — select-all only
   * governs the items the picker can actually change.
   */
  public allCheckedForPo(po: SofPoNumber): boolean {
    const items = this.itemsForPo(po).filter((i) => !i.alreadyPulled);
    return items.length > 0 && items.every((i) => i.checkedOut);
  }

  /** True when a PO has at least one item the picker can still toggle. */
  public hasToggleableItems(po: SofPoNumber): boolean {
    return this.itemsForPo(po).some((i) => !i.alreadyPulled);
  }

  public toggleSelectAllForPo(po: SofPoNumber): void {
    if (!this.hasToggleableItems(po)) {
      return;
    }
    this.state.setAllItems(!this.allCheckedForPo(po), po.poNumber);
  }

  public toggleItem(item: SofItemEdit): void {
    this.state.toggleItem(item.id);
  }

  public toggleNotesEditor(item: SofItemEdit, event: Event): void {
    event.stopPropagation();
    this.openNotesItemId = this.openNotesItemId === item.id ? null : item.id;
  }

  public onNotesChange(item: SofItemEdit, event: any): void {
    this.state.updateItemNotes(item.id, event?.detail?.value ?? '');
  }

  /**
   * The staging area is always uppercase — the value that reaches
   * `SOF.Orders.ReceivingStagingArea` (and the confirmation panel) must be
   * uppercase, not just the on-screen display, so it's normalized here rather
   * than only via CSS `text-transform`.
   */
  public onStagingAreaChange(event: any): void {
    this.state.stagingArea = (event?.detail?.value ?? '').toUpperCase();
  }

  /** The first location chip for an item (used in the condensed stage row). */
  public firstLocation(item: SofItemEdit): string {
    return this.locationChips(item)[0] ?? '';
  }

  /**
   * trackBy for item loops. Without this, editing a note rebuilds the items
   * array on every keystroke and Angular recreates the row (and its textarea),
   * so the field loses focus after one character. Keying by id lets Angular
   * reuse the DOM node.
   */
  public trackByItemId(_index: number, item: SofItemEdit): number {
    return item.id;
  }

  public trackByPo(_index: number, po: SofPoNumber): string {
    return po.poNumber;
  }

  // ── Proof photos ──

  /**
   * Persisted photos are deletable while editing and until receiving is
   * Complete (the server enforces the same rule).
   */
  public get canDeletePhotos(): boolean {
    return canDeleteSofPhotos(
      this.readOnly,
      this.state.sofDetails.value?.receivingStatus
    );
  }

  /** New captures from the uploader → the pending set (uploads on save). */
  public onPhotosPicked(photos: LogEntryPhoto[]): void {
    this.state.addNewPhotos(photos);
  }

  /** Removing a not-yet-uploaded photo is local only — no confirm needed. */
  public deleteNewPhoto(index: number): void {
    this.state.removeNewPhoto(index);
  }

  public deleteAllNewPhotos(): void {
    if (!confirm('Remove all photos that have not been uploaded yet?')) {
      return;
    }
    this.state.clearNewPhotos();
  }

  public deleteExistingPhoto(index: number): void {
    if (!confirm('Are you sure you want to delete this photo from the SOF?')) {
      return;
    }
    this.state.deletePhoto(index);
  }

  public deleteAllExistingPhotos(): void {
    if (!confirm('Are you sure you want to delete all photos from this SOF?')) {
      return;
    }
    this.state.deleteAllPhotos();
  }

  // ── Step navigation ──

  public goToStage(): void {
    if (this.state.checkedCount === 0) {
      return;
    }
    this.step = 'stage';
  }

  public backToPick(): void {
    this.step = 'pick';
  }

  // ── Actions ──

  public save(): void {
    this.state.save();
  }

  public markComplete(): void {
    this.state.markComplete();
  }

  /** Confirmation panel → back to the queue. */
  public finishCompletion(): void {
    this.state.finishCompletion();
    this.backToQueue.emit();
  }

  /**
   * PO report placeholder — opens the PoReportViewerComponent modal with the
   * PO number. Phoenix will replace that component's internals with the
   * embedded RDLC report; this click handler stays as-is.
   */
  public async openPoReport(po: SofPoNumber, event: Event): Promise<void> {
    event.stopPropagation();
    const modal = await this._modalController.create({
      component: PoReportViewerComponent,
      componentProps: { poNumber: po.poNumber },
    });
    await modal.present();
  }
}
